import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles, categories, type Article } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
    const found = articles.find((a) => a.id === id);
    setArticle(found || null);

    if (found) {
      const related = articles
        .filter((a) => a.category === found.category && a.id !== found.id)
        .slice(0, 3);
      setRelatedArticles(related);
    }
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
            <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-500 mb-4">文章未找到</p>
          <Link
            to="/"
            className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === article.category);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.publishDate,
            author: { '@type': 'Person', name: article.author },
            image: article.coverImage,
            publisher: {
              '@type': 'Organization',
              name: '朝桐光官方粉丝资讯站',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://plushy.cn/' },
              {
                '@type': 'ListItem',
                position: 2,
                name: categoryInfo?.name || '',
                item: `https://plushy.cn/category/${article.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
                item: `https://plushy.cn/article/${article.id}`,
              },
            ],
          }),
        }}
      />

      <article className="min-h-screen pt-16">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryInfo?.name || '', path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-3xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-sans bg-accent-100 text-accent-800">
                  {categoryInfo?.name}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs text-foreground-400">
                    #{tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 leading-snug mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()} 阅读
                </span>
              </div>
            </header>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
                title={article.title}
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                [&_h3]:font-heading [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            ></div>

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 mr-1">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs font-sans bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 rounded-lg bg-background-100">
              <p className="text-sm text-foreground-600">
                分享这篇文章给更多的粉丝朋友
              </p>
              <div className="flex items-center gap-2">
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} />
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </>
  );
}